import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePlayer } from "../../contexts/PlayerContext";

export const ExternalPlayerBridge: React.FC = () => {
  const {
    currentTrack,
    isPlaying,
    pauseTrack,
    resumeTrack,
    nextTrack,
    setProgress,
    setDuration,
    setExternalControls,
  } = usePlayer();
  const mixcloudWidgetRef = useRef<any>(null);
  const [widgetReady, setWidgetReady] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  // Initialize Mixcloud Widget Script
  useEffect(() => {
    if (!document.getElementById("mixcloud-widget-api")) {
      const script = document.createElement("script");
      script.id = "mixcloud-widget-api";
      script.src = "https://widget.mixcloud.com/media/js/widgetApi.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Find portal target
  useEffect(() => {
    const target = document.getElementById("external-player-container");
    setPortalTarget(target);

    // Set up a mutation observer just in case the panel is toggled and ID disappears/reappears
    const observer = new MutationObserver(() => {
      const newTarget = document.getElementById("external-player-container");
      if (newTarget !== portalTarget) {
        setPortalTarget(newTarget);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [portalTarget]);

  // Handle Mixcloud Widget lifecycle
  useEffect(() => {
    if (currentTrack?.source === "MIXCLOUD" && currentTrack.audioUrl) {
      const initializeWidget = () => {
        const iframe = document.getElementById(
          "mixcloud-iframe",
        ) as HTMLIFrameElement;

        if (iframe && (window as any).Mixcloud) {
          const widget = (window as any).Mixcloud.PlayerWidget(iframe);
          widget.ready.then(() => {
            mixcloudWidgetRef.current = widget;

            // Push commands up to global context
            setExternalControls({
              play: () => widget.play(),
              pause: () => widget.pause(),
              seekTo: (time: number) => widget.seek(time),
            });

            setWidgetReady(true);

            // Sync from Widget -> Global Context
            widget.events.pause.on(() => pauseTrack());
            widget.events.play.on(() => resumeTrack());
            widget.events.ended.on(() => nextTrack());

            widget.events.progress.on((progress: number, duration: number) => {
              setProgress(progress);
              if (duration) setDuration(duration);
            });

            // If it's supposed to be playing right now, command the widget to play
            if (isPlaying) {
              widget.play();
            }
          });
        } else {
          // Retry initialization if script hasn't loaded yet
          setTimeout(initializeWidget, 500);
        }
      };

      initializeWidget();
    } else {
      // Clean up or pause if switching away
      if (mixcloudWidgetRef.current) {
        mixcloudWidgetRef.current.pause();
      }
      setWidgetReady(false);
      setExternalControls(null);
    }
  }, [currentTrack?.audioUrl, currentTrack?.source]);

  // Sync state changes from our global player to the widget
  useEffect(() => {
    if (widgetReady && mixcloudWidgetRef.current) {
      if (isPlaying) {
        mixcloudWidgetRef.current.play();
      } else {
        mixcloudWidgetRef.current.pause();
      }
    }
  }, [isPlaying, widgetReady]);

  if (!currentTrack || currentTrack.source === "LOCAL" || !portalTarget)
    return null;

  const content = (
    <div className="w-full h-full flex items-center justify-center bg-black">
      {currentTrack.source === "MIXCLOUD" && (
        <iframe
          id="mixcloud-iframe"
          width="100%"
          height="120"
          src={`https://www.mixcloud.com/widget/iframe/?hide_cover=1&mini=1&light=0&feed=${encodeURIComponent(new URL(currentTrack.audioUrl).pathname)}`}
          frameBorder="0"
          allow="autoplay"
          className="rounded shadow-lg"
        />
      )}
    </div>
  );

  return createPortal(content, portalTarget);
};
